import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { signOut } from 'firebase/auth'
import { firebaseAuth } from './firebase'

// React equivalent of vanilla `authFetch` in app/js/auth.js. Adds the Firebase
// JWT + tenant header on every call, matching server expectations.
//
// Server uses 'X-Tenant-ID' (see app/auth.py:277). For super admins, this
// header is required — without it the server can't pick a tenant and returns
// 401. For regular users the server derives tenant from the JWT claims and
// the header is ignored, so it's safe to always send.
export async function authFetch(
  url: string,
  opts: RequestInit = {},
  tenantOverride?: string | null,
): Promise<Response> {
  const user = firebaseAuth.currentUser
  const headers = new Headers(opts.headers)
  if (user) {
    const token = await user.getIdToken()
    headers.set('Authorization', `Bearer ${token}`)
  }
  // Tenant header: callers may pass the tenant the UI is CURRENTLY showing
  // (useAuth().activeTenant, which falls back to me.tenant_id). Without an
  // explicit override we read sessionStorage — but that has no me.tenant_id
  // fallback, so a super-admin whose nav shows a tenant via that fallback would
  // otherwise send no header and get their home tenant's data instead. Passing
  // the override keeps the data tenant locked to the displayed tenant.
  const activeTenant = tenantOverride || sessionStorage.getItem('activeTenant')
  if (activeTenant) headers.set('X-Tenant-ID', activeTenant)
  const resp = await fetch(url, { ...opts, headers })

  // 401 handling: distinguish "token expired" from "endpoint doesn't like
  // this request" (missing tenant context, permission, sparse data).
  // Force-refresh the token; if THAT fails the token is truly dead — sign
  // out + redirect. If it succeeds, propagate the 401 so the page can render
  // its own error state without nuking the user's session.
  if (resp.status === 401 && user) {
    let tokenStillValid = false
    try {
      await user.getIdToken(true)  // force refresh
      tokenStillValid = true
    } catch { /* token genuinely dead */ }

    if (!tokenStillValid) {
      console.warn(`Auth 401 on ${url} and token refresh failed — signing out`)
      sessionStorage.removeItem('activeTenant')
      sessionStorage.removeItem('userContext')
      sessionStorage.removeItem('tenantFeatures')
      try { await signOut(firebaseAuth) } catch { /* already signed out */ }
      window.location.href = '/?expired=1'
    } else {
      console.warn(`401 on ${url} but token is still valid — propagating to caller`)
    }
  }
  return resp
}

// useApi hook — thin wrapper over useQuery that uses authFetch and parses JSON.
// Returns standard React Query state (data/loading/error/refetch).
//
// The queryKey includes activeTenant — without this, super admins who switch
// tenants would see CACHED data from the previous tenant for the 5-min
// staleTime window. The same URL serves different data per tenant, so the
// tenant id has to be part of the cache key.
export function useApi<T = unknown>(
  url: string,
  opts: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> = {},
) {
  const activeTenant = typeof window !== 'undefined'
    ? sessionStorage.getItem('activeTenant') || ''
    : ''
  return useQuery<T>({
    queryKey: [url, activeTenant],
    queryFn: async () => {
      const r = await authFetch(url)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    ...opts,
  })
}
